/**
 * Amazon sales ledger (Faraz brief, 2026-08-25: "a page to show all amazon
 * sales, with all sorting, filters, use the complete real estate, no sorry
 * rows"). /app/sales, OPS group, module-gated on 'amazon'. Full-width
 * dense table (max-w-7xl — the widest surface in the app; every other OPS
 * screen uses max-w-6xl, this one earns the extra column). Toolbar:
 * date-range (re-fetches), status pills DERIVED from the statuses actually
 * present in the fetched range (never a hardcoded guess), search (sku /
 * product / order id). Totals strip reflects the current filtered view.
 * All filter/sort/search/totals logic is pure helpers in lib/sales.ts,
 * unit-tested there — this file is rendering only.
 */
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown, Search } from 'lucide-react';
import { Badge, cn, Card, Input } from '../../design';
import {
  capNoticeMessage,
  compareSalesRows,
  DEFAULT_SALES_CHANNEL_FILTER,
  DEFAULT_SALES_STATUS_FILTER,
  defaultSalesDateRange,
  filterSalesRowsByChannel,
  filterSalesRowsByStatus,
  fulfillmentChannelBadge,
  salesChannelPills,
  salesEmptyStateMessage,
  salesStatusPills,
  salesTotals,
  searchSalesRows,
  sortSalesRows,
  statusBadge,
  totalLabel,
  unitPriceLabel,
  useSales,
  type SalesChannelFilter,
  type SalesSortDirection,
  type SalesSortKey,
  type SalesStatusFilter,
} from '../../lib/sales';
import { shortDateLabel } from '../../lib/shipments';
import { useModuleEnabled } from '../../lib/auth';
import { AppShell } from '../../components/AppShell';
import { ProductThumb } from '../../components/ProductThumb';
import { ProductLink } from '../../components/ProductLink';
import { SkeletonRows } from '../../components/Skeleton';
import type { SalesRow } from '@sourcito/schemas';

export function SalesPage() {
  const enabled = useModuleEnabled('amazon');
  const [dateRange, setDateRange] = useState(() => defaultSalesDateRange());
  const { data, isLoading, isError } = useSales(dateRange, enabled);

  if (!enabled) {
    return (
      <AppShell>
        <main className="max-w-3xl mx-auto px-8 py-16">
          <Card>
            <p className="text-sm text-fg-secondary">
              The Amazon module isn&rsquo;t enabled for this workspace.
            </p>
          </Card>
        </main>
      </AppShell>
    );
  }

  const rows = data?.rows ?? [];
  const meta = data?.meta ?? { rowCount: 0, capped: false };

  return (
    <AppShell>
      <header className="border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl font-medium leading-tight">Sales</h1>
              <p className="text-xs text-fg-tertiary">
                {rows.length > 0 ? `${rows.length} order line${rows.length === 1 ? '' : 's'}` : ' '}
              </p>
            </div>
            <DateRangeControls range={dateRange} onChange={setDateRange} />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-8">
        {isLoading && <SalesTableSkeleton />}
        {isError && <p className="text-sm text-negative">Could not load sales.</p>}
        {!isLoading && !isError && <SalesTable rows={rows} meta={meta} />}
      </main>
    </AppShell>
  );
}

function DateRangeControls({
  range,
  onChange,
}: {
  range: { from: string; to: string };
  onChange: (range: { from: string; to: string }) => void;
}) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <label className="flex items-center gap-1.5 text-xs text-fg-tertiary">
        From
        <Input
          type="date"
          inputSize="sm"
          value={range.from}
          onChange={(e) => onChange({ ...range, from: e.target.value })}
          className="w-[150px]"
        />
      </label>
      <label className="flex items-center gap-1.5 text-xs text-fg-tertiary">
        To
        <Input
          type="date"
          inputSize="sm"
          value={range.to}
          onChange={(e) => onChange({ ...range, to: e.target.value })}
          className="w-[150px]"
        />
      </label>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Totals strip
// ─────────────────────────────────────────────────────────

function TotalsStrip({ rows }: { rows: SalesRow[] }) {
  const totals = useMemo(() => salesTotals(rows), [rows]);
  return (
    <div className="grid grid-cols-3 gap-3 mb-4">
      <TotalTile label="Orders" value={totals.orderCount.toLocaleString('en-US')} />
      <TotalTile label="Units" value={totals.units.toLocaleString('en-US')} />
      <TotalTile
        label="Gross revenue"
        value={totalLabel(totals.revenueCents)}
        hint="Item price sums, not profit"
      />
    </div>
  );
}

function TotalTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="bg-surface border border-border-subtle rounded-xl px-4 py-3">
      <p className="text-[10px] uppercase tracking-[0.14em] text-fg-tertiary">{label}</p>
      <p className="font-display text-2xl tabular-nums text-fg-primary mt-0.5">{value}</p>
      {hint && <p className="text-[10px] text-fg-tertiary mt-0.5">{hint}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Density table
// ─────────────────────────────────────────────────────────

/** Shimmer placeholder for SalesTable while the sales query is loading —
 *  totals strip and toolbar are omitted (their inputs aren't known yet),
 *  just the table shape sized to the real SalesTableRow columns. */
function SalesTableSkeleton() {
  return (
    <div className="mt-3 bg-surface border border-border-subtle rounded-xl overflow-x-auto">
      <table className="w-full text-sm min-w-[1100px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.14em] text-fg-tertiary text-left">
            <th className="px-4 py-2 font-medium">Date</th>
            <th className="px-4 py-2 font-medium">Order ID</th>
            <th className="px-4 py-2 font-medium">Product</th>
            <th className="px-4 py-2 font-medium text-right">Qty</th>
            <th className="px-4 py-2 font-medium text-right">Unit price</th>
            <th className="px-4 py-2 font-medium text-right">Total</th>
            <th className="px-4 py-2 font-medium text-center">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          <SkeletonRows
            rows={6}
            colWidths={['w-16', 'w-24', 'w-40', 'w-8', 'w-14', 'w-14', 'w-16']}
          />
        </tbody>
      </table>
    </div>
  );
}

function SalesTable({
  rows,
  meta,
}: {
  rows: SalesRow[];
  meta: { rowCount: number; capped: boolean };
}) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<SalesStatusFilter>(DEFAULT_SALES_STATUS_FILTER);
  const [channelFilter, setChannelFilter] = useState<SalesChannelFilter>(
    DEFAULT_SALES_CHANNEL_FILTER
  );
  const [sortKey, setSortKey] = useState<SalesSortKey>('date');
  const [sortDirection, setSortDirection] = useState<SalesSortDirection>('desc');

  const pills = useMemo(() => salesStatusPills(rows), [rows]);
  const channelPills = useMemo(() => salesChannelPills(rows), [rows]);

  const visibleRows = useMemo(() => {
    const searched = searchSalesRows(rows, query);
    const statusFiltered = filterSalesRowsByStatus(searched, statusFilter);
    const filtered = filterSalesRowsByChannel(statusFiltered, channelFilter);
    return sortSalesRows(filtered, sortKey, sortDirection);
  }, [rows, query, statusFilter, channelFilter, sortKey, sortDirection]);

  const capNotice = capNoticeMessage(meta);

  function toggleSort(key: SalesSortKey) {
    if (key === sortKey) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection(key === 'date' ? 'desc' : 'asc');
    }
  }

  return (
    <div>
      <TotalsStrip rows={visibleRows} />

      {capNotice && (
        <p className="text-xs text-warning bg-warning/[0.08] border border-warning/30 rounded-lg px-3 py-2 mb-4">
          {capNotice}
        </p>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="Order status">
        {pills.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={statusFilter === p.key}
            onClick={() => setStatusFilter(p.key)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast',
              statusFilter === p.key
                ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
            )}
          >
            {p.label} · {p.count}
          </button>
        ))}
      </div>

      {/* Channel pills — only rendered once a channel is actually present in
          the fetched range (older rows predate migration 0053 and carry a
          null channel; there's nothing useful to filter on for those). */}
      {channelPills.length > 1 && (
        <div
          className="mt-2 flex items-center gap-2 flex-wrap"
          role="tablist"
          aria-label="Fulfillment channel"
        >
          {channelPills.map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={channelFilter === p.key}
              onClick={() => setChannelFilter(p.key)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast',
                channelFilter === p.key
                  ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                  : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
              )}
            >
              {p.label} · {p.count}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 relative max-w-sm">
        <Search
          className="h-4 w-4 text-fg-tertiary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          aria-hidden="true"
        />
        <Input
          inputSize="sm"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search SKU, product, or order id..."
          className="pl-9"
        />
      </div>

      {/* Table */}
      <div className="mt-3 bg-surface border border-border-subtle rounded-xl overflow-x-auto">
        {visibleRows.length === 0 ? (
          <p className="text-sm text-fg-secondary px-5 py-6">
            {salesEmptyStateMessage(statusFilter, query)}
          </p>
        ) : (
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.14em] text-fg-tertiary text-left">
                <SortableTh
                  label="Date"
                  sortKey="date"
                  active={sortKey === 'date'}
                  direction={sortDirection}
                  onClick={() => toggleSort('date')}
                />
                <SortableTh
                  label="Order ID"
                  sortKey="orderId"
                  active={sortKey === 'orderId'}
                  direction={sortDirection}
                  onClick={() => toggleSort('orderId')}
                />
                <SortableTh
                  label="Product"
                  sortKey="product"
                  active={sortKey === 'product'}
                  direction={sortDirection}
                  onClick={() => toggleSort('product')}
                />
                <SortableTh
                  label="Qty"
                  sortKey="qty"
                  active={sortKey === 'qty'}
                  direction={sortDirection}
                  onClick={() => toggleSort('qty')}
                  align="right"
                />
                <SortableTh
                  label="Unit price"
                  sortKey="unitPrice"
                  active={sortKey === 'unitPrice'}
                  direction={sortDirection}
                  onClick={() => toggleSort('unitPrice')}
                  align="right"
                />
                <SortableTh
                  label="Total"
                  sortKey="total"
                  active={sortKey === 'total'}
                  direction={sortDirection}
                  onClick={() => toggleSort('total')}
                  align="right"
                />
                <SortableTh
                  label="Status"
                  sortKey="status"
                  active={sortKey === 'status'}
                  direction={sortDirection}
                  onClick={() => toggleSort('status')}
                  align="center"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {visibleRows.map((row) => (
                <SalesTableRow key={row.id} row={row} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SortableTh({
  label,
  active,
  direction,
  onClick,
  align = 'left',
}: {
  label: string;
  sortKey: SalesSortKey;
  active: boolean;
  direction: SalesSortDirection;
  onClick: () => void;
  align?: 'left' | 'right' | 'center';
}) {
  const Icon = active ? (direction === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <th
      className={cn(
        'px-4 py-2 font-medium',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center'
      )}
    >
      <button
        type="button"
        onClick={onClick}
        aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={cn(
          'inline-flex items-center gap-1 hover:text-fg-secondary transition-colors duration-fast',
          align === 'right' && 'flex-row-reverse',
          align === 'center' && 'mx-auto'
        )}
      >
        {label}
        <Icon
          className={cn('h-3 w-3', active ? 'text-fg-secondary' : 'text-fg-tertiary')}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}

function SalesTableRow({ row }: { row: SalesRow }) {
  const label = row.productName ?? row.sellerSku;
  const badge = statusBadge(row.orderStatus);
  const channelBadge = fulfillmentChannelBadge(row.fulfillmentChannel);

  return (
    <tr>
      {/* Date */}
      <td className="px-4 py-3 align-top text-xs text-fg-tertiary whitespace-nowrap">
        {shortDateLabel(row.purchaseDate)}
      </td>

      {/* Order ID */}
      <td className="px-4 py-3 align-top text-xs font-mono text-fg-secondary whitespace-nowrap">
        {row.amazonOrderId}
      </td>

      {/* Product */}
      <td className="px-4 py-3 align-top">
        <div className="flex items-start gap-3 min-w-0">
          <ProductThumb imageUrl={row.imageUrl} alt={label} size="sm" />
          <div className="min-w-0">
            <p className="text-sm text-fg-primary line-clamp-2" title={label}>
              <ProductLink asin={row.asin}>{label}</ProductLink>
            </p>
            <p className="text-xs font-mono text-fg-tertiary truncate">{row.sellerSku}</p>
          </div>
        </div>
      </td>

      {/* Qty */}
      <td className="px-4 py-3 align-top text-right tabular-nums">{row.qty}</td>

      {/* Unit price */}
      <td className="px-4 py-3 align-top text-right tabular-nums">
        {unitPriceLabel(row.unitPriceCents)}
      </td>

      {/* Total */}
      <td className="px-4 py-3 align-top text-right tabular-nums">
        {totalLabel(row.rowTotalCents)}
      </td>

      {/* Status */}
      <td className="px-4 py-3 align-top text-center">
        <div className="flex flex-col items-center gap-1">
          <Badge variant={badge.variant}>{badge.label}</Badge>
          {channelBadge && <Badge variant={channelBadge.variant}>{channelBadge.label}</Badge>}
        </div>
      </td>
    </tr>
  );
}
